import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { Box, LinearProgress } from '@mui/material'
import { ui, tone } from '../../../utils/uiTokens'
import { CRM_POLL, CrmCountSum, crmApi } from '../../../features/crm/crmApi'
import { Card, CardHead, Empty, Hint, Kpis, Muted, PageHead, PeriodBar, TableWrap, contentSx, pageSx, usePeriod } from '../shared/crmUi'
import { dayKey, dictLabel, fmtDate, money } from '../shared/format'
import { useCrmDict } from '../shared/dict'
import { EmptyRow } from '../head/HeadCrmNewPage'

const MAX_DAYS = 93

const RopReportPage = () => {
    const { formatMessage } = useIntl()
    const period = usePeriod('month')
    const range = { from: period.from, to: period.to }
    const { data: r, isFetching } = crmApi.useGetSalesReportQuery(range, { pollingInterval: CRM_POLL })
    const { data: managers = [] } = crmApi.useGetManagersQuery(range)
    const byId = useMemo(() => new Map(managers.map((m) => [m.userId, m])), [managers])
    const dict = useCrmDict()

    const byManager = [...(r?.byManager ?? [])].sort((a, b) => b.turnover - a.turnover)

    return (
        <Box sx={pageSx}>
            <Box sx={contentSx}>
                <PageHead title={formatMessage({ id: 'crmRopReport' })} sub={formatMessage({ id: 'crmRopReportSub' })} />
                <Card sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <PeriodBar period={period} />
                    {isFetching && <LinearProgress />}
                    <Kpis
                        items={[
                            { label: formatMessage({ id: 'crmKpiStudents' }), value: r?.count ?? 0 },
                            {
                                label: formatMessage({ id: 'crmKpiTurnover' }),
                                value: money(r?.turnover),
                                sub: r?.installmentTurnover ? formatMessage({ id: 'crmMonthlyInsideSum' }, { sum: money(r.installmentTurnover) }) : undefined,
                            },
                            { label: formatMessage({ id: 'crmKpiAvg' }), value: money(r?.avgCheck) },
                            { label: formatMessage({ id: 'crmKpiDiscount' }), value: money(r?.discount) },
                            { label: formatMessage({ id: 'crmKpiBooks' }), value: money(r?.books) },
                            {
                                label: formatMessage({ id: 'crmKpiReturns' }),
                                value: r?.returns ?? 0,
                                sub: r?.returnsSum ? money(r.returnsSum) : undefined,
                                bad: !!r?.returns,
                            },
                        ]}
                    />
                    <Hint>{formatMessage({ id: 'crmPeriodLine' }, { from: fmtDate(period.from), to: fmtDate(period.to) })}</Hint>
                </Card>

                <Card>
                    <CardHead title={formatMessage({ id: 'crmByDay' })} />
                    <DayChart from={period.from} to={period.to} days={r?.byDay ?? []} />
                </Card>

                <Card>
                    <CardHead title={formatMessage({ id: 'crmManagersRating' })} />
                    <TableWrap minWidth={680}>
                        <table>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{formatMessage({ id: 'crmManager' })}</th>
                                    <th className="num">{formatMessage({ id: 'crmKpiStudents' })}</th>
                                    <th className="num">{formatMessage({ id: 'crmKpiTurnover' })}</th>
                                    <th className="num">{formatMessage({ id: 'crmKpiAvg' })}</th>
                                    <th className="num">{formatMessage({ id: 'crmKpiDiscount' })}</th>
                                    <th className="num">{formatMessage({ id: 'crmKpiReturns' })}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {byManager.map((m, i) => (
                                    <tr key={m.sellerId}>
                                        <td>{i + 1}</td>
                                        <td>
                                            <b>{m.sellerName || '-'}</b>
                                            {byId.get(m.sellerId) && (
                                                <Muted>
                                                    {dictLabel(formatMessage, 'Group', byId.get(m.sellerId)?.salesGroup)}
                                                    {byId.get(m.sellerId)?.firedAt ? ` · ${formatMessage({ id: 'crmFiredLower' })}` : ''}
                                                </Muted>
                                            )}
                                        </td>
                                        <td className="num">{m.count}</td>
                                        <td className="num">
                                            <b>{money(m.turnover)}</b>
                                        </td>
                                        <td className="num">{money(m.avgCheck)}</td>
                                        <td className="num">{money(m.discount)}</td>
                                        <td className="num" style={{ color: m.returns ? tone.bad.text : undefined }}>{m.returns}</td>
                                    </tr>
                                ))}
                                {!byManager.length && <EmptyRow cols={7} text={formatMessage({ id: 'crmPeriodEmpty' })} />}
                            </tbody>
                        </table>
                    </TableWrap>
                </Card>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    <Card sx={{ marginBottom: 0 }}>
                        <CardHead title={formatMessage({ id: 'crmByPackage' })} />
                        <BarList label={(k) => dict.label('PACKAGE', k)} items={r?.byPackage ?? []} bySum />
                    </Card>
                    <Card sx={{ marginBottom: 0 }}>
                        <CardHead title={formatMessage({ id: 'crmBySource' })} />
                        <BarList label={(k) => dict.label('SOURCE', k)} items={r?.bySource ?? []} />
                    </Card>
                    <Card sx={{ marginBottom: 0 }}>
                        <CardHead title={formatMessage({ id: 'crmByPayType' })} />
                        <BarList label={(k) => dict.label('PAY_TYPE', k)} items={r?.byPayType ?? []} />
                    </Card>
                    <Card sx={{ marginBottom: 0 }}>
                        <CardHead title={formatMessage({ id: 'crmByPayment' })} />
                        <BarList label={(k) => dict.label('PAY_METHOD', k)} items={r?.byPayment ?? []} />
                    </Card>
                </Box>
            </Box>
        </Box>
    )
}

// bySum: полоса = сумма, подпись «N оқушы»; иначе полоса = число учеников, подпись - сумма
const BarList: React.FC<{ label: (key: string) => string; items: CrmCountSum[]; bySum?: boolean }> = ({ label, items, bySum }) => {
    const { formatMessage } = useIntl()
    const value = (x: CrmCountSum) => (bySum ? x.sum : x.count)
    const sorted = [...items].sort((a, b) => b.sum - a.sum)
    const max = Math.max(1, ...sorted.map(value))
    if (!sorted.length) return <Empty />
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {sorted.map((x) => (
                <Box key={x.key} sx={{ fontSize: '13px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <span>{label(x.key)}</span>
                        <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                            <b>{bySum ? money(x.sum) : formatMessage({ id: 'crmStudentsN' }, { n: x.count })}</b>
                            <Box component="span" sx={{ color: ui.muted }}>
                                {' · '}
                                {bySum ? formatMessage({ id: 'crmStudentsN' }, { n: x.count }) : money(x.sum)}
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ height: 8, background: ui.track, borderRadius: 4, marginTop: '4px', overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', width: `${Math.round((value(x) * 100) / max)}%`, background: ui.pink, borderRadius: 4 }} />
                    </Box>
                </Box>
            ))}
        </Box>
    )
}

// «1,2 млн» / «85 мың» над столбцом
const short = (v: number, fm: (d: { id: string }, v?: any) => string) =>
    !v ? '' : v >= 1e6 ? fm({ id: 'crmMln' }, { n: (v / 1e6).toFixed(2).replace(/\.?0+$/, '').replace('.', ',') }) : fm({ id: 'crmThousand' }, { n: Math.round(v / 1000) })

const DayChart: React.FC<{ from: string; to: string; days: CrmCountSum[] }> = ({ from, to, days }) => {
    const { formatMessage } = useIntl()
    const sums = new Map(days.map((d) => [d.key, d.sum]))
    // «Барлығы» - с 2020 года: график начинаем с первого дня с продажами
    const firstSale = days.filter((d) => d.sum > 0).map((d) => d.key).sort()[0]
    let start = new Date(`${from}T00:00:00`)
    const end = new Date(`${to}T00:00:00`)
    if ((end.getTime() - start.getTime()) / 86400000 > MAX_DAYS && firstSale) start = new Date(`${firstSale}T00:00:00`)
    const keys: string[] = []
    for (let d = new Date(start); d <= end && keys.length < 400; d.setDate(d.getDate() + 1)) keys.push(dayKey(d))
    const shown = keys.slice(-Math.max(MAX_DAYS, 1))
    const values = shown.map((k) => sums.get(k) || 0)
    const max = Math.max(1, ...values)
    const many = shown.length > 24
    if (!days.length) return <Empty />

    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ display: 'flex', gap: many ? '3px' : '6px', alignItems: 'stretch', minWidth: Math.max(shown.length * (many ? 34 : 44), 280) }}>
                {shown.map((k, i) => (
                    <Box
                        key={k}
                        title={`${fmtDate(k)} · ${money(values[i])}`}
                        sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}
                    >
                        <Box sx={{ fontSize: many ? '9.5px' : '11px', fontWeight: 700, color: ui.ink, whiteSpace: 'nowrap', minHeight: 14 }}>
                            {short(values[i], formatMessage)}
                        </Box>
                        <Box sx={{ height: 140, width: '100%', display: 'flex', alignItems: 'flex-end', background: ui.surface, borderRadius: '4px' }}>
                            <Box sx={{ width: '100%', height: `${(values[i] * 100) / max}%`, minHeight: values[i] ? 2 : 0, background: ui.pink, borderRadius: '4px' }} />
                        </Box>
                        <Box sx={{ fontSize: many ? '9.5px' : '11px', color: ui.muted, whiteSpace: 'nowrap' }}>{`${k.slice(8, 10)}.${k.slice(5, 7)}`}</Box>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

export default RopReportPage
